import useTranslation from 'next-translate/useTranslation';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CroppedImage } from '@weee-fe/module';
import { claimSellerCoupon } from '@/api/marketplace';
import dynamic from 'next/dynamic';
import { getLoginState } from '@/utils/common/user';
import useImpressionT2 from '@/tracks/useImpressionT2';
import useClickActionT2 from '@/tracks/useClickActionT2';
import ImpTracker from '@/components/common/ImpTracker';
import { twMerge } from 'tailwind-merge';
import { PlusFilled, WavyCheckFilled, ArrowRight, TagFilled } from '@weee-fe/kunlunicon';
import Swiper, { SwiperSlide, SwiperTypes } from '@/components/common/Swiper';
import { useInView } from 'react-intersection-observer';
import { isValidArray } from '@/utils/isValidArray';
import Trans from 'next-translate/Trans';

type Props = {
  info?: any;
  isUseInCms?: boolean;
  index?: number;
  modNm?: string;
  modPos?: number;
  animationPlay?: boolean;
  rootMargin?: string;
  handleChange?: (inView: boolean, seller_id: number) => void;
};

const CouponLeftTime = dynamic(() => import('../CouponLeftTime'), {
  ssr: false
});

const CouponDetailsModal = dynamic(() => import('../CouponDetailsModal'), {
  ssr: false
});

const ANIMATION_DELAY = 4800;
const ANIMATION_TRANSITION = 500;

const CouponCard: React.FC<Props> = ({
  info,
  isUseInCms = false,
  index,
  modNm,
  modPos,
  animationPlay = false,
  rootMargin = '0px 0px 0px 0px',
  handleChange
}) => {
  const { products, seller_id, seller_title, promotions_info, seller_logo_url, id, plan_id, end_time } = info;
  const { lang } = useTranslation('vendor');
  const { bannerImpression } = useImpressionT2();
  const { commonClick } = useClickActionT2();
  const [showModal, setShowModal] = useState<boolean>(false);
  const [expired, setExpired] = useState<boolean>(false);
  const [isClaimed, setIsClaimed] = useState<boolean>(!!id ? true : false);
  const [endTime, setEndTime] = useState<number>(end_time);
  const [claiming, setClaiming] = useState<boolean>(false);
  const [couponId, setCouponId] = useState<number>(id);
  const currSwiperRef = useRef<any>(null);
  const productWrapperRef = useRef<HTMLDivElement | null>(null);
  const [productWidth, setProductWidth] = useState<number | null>(null); // 用于存储元素的宽度

  const [ref] = useInView({
    threshold: 1,
    rootMargin,
    onChange: inView => {
      handleChange?.(inView, seller_id);
    }
  });

  /** 将一维数组按照长度截取为二维数组 */
  const chunkArray = (arr, chunkSize) => {
    const chunkedArray = [];
    for (let i = 0; i < arr.length; i += chunkSize) {
      chunkedArray.push(arr.slice(i, i + chunkSize));
    }
    return chunkedArray;
  };

  const handleSetExpired = useCallback(status => {
    setExpired(status);
  }, []);

  const handleClaimCoupon = async e => {
    e.stopPropagation();
    const isLogin = getLoginState();
    if (!isLogin) {
      const curUrl = encodeURIComponent(window.location.href);
      window.location.href = `/${lang}/account/login?redirect_url=${curUrl}`;
      return;
    }
    if (isClaimed || expired || claiming) return;
    setClaiming(true);
    commonClick({
      mod_nm: modNm || 'coupon_list',
      mod_pos: modPos || 2,
      co: {
        target_nm: 'claim',
        target_type: 'normal_button',
        target_pos: index,
        click_type: 'normal'
      },
      ctx: {
        global_vendor: info?.seller_id,
        tag_id: plan_id
      }
    });
    const res = await claimSellerCoupon(seller_id, { plan_id: plan_id });
    if (res?.result) {
      setEndTime(res?.object?.apply_expiration_time);
      setShowModal(true);
      setIsClaimed(true);
      setCouponId(res?.object?.coupon_id);
    }
    setClaiming(false);
  };

  const handleUseCoupon = e => {
    e.stopPropagation();
    if (!info?.use_url) return;
    commonClick({
      mod_nm: modNm || 'coupon_list',
      mod_pos: modPos || 2,
      co: {
        target_nm: 'use',
        target_type: 'normal_button',
        target_pos: index,
        click_type: 'normal'
      },
      ctx: {
        global_vendor: info?.seller_id,
        tag_id: id
      }
    });
    window.location.href = info?.use_url;
  };

  const handleCouponSection = e => {
    e.stopPropagation();
    if (!info?.use_url) return;
    commonClick({
      mod_nm: modNm || 'coupon_list',
      mod_pos: modPos || 2,
      co: {
        target_nm: id || plan_id,
        target_type: 'coupon',
        target_pos: index,
        click_type: 'view',
        url: info?.use_url
      },
      ctx: {
        global_vendor: seller_id,
        status: !!id ? 'coupon' : 'coupon_plan'
      }
    });
    window.location.href = info?.use_url;
  };

  const handleClose = useCallback(() => {
    setShowModal(false);
  }, []);

  const onSlideChange = useCallback(async (swiper: SwiperTypes) => {
    currSwiperRef.current = swiper;
  }, []);

  const productArray = useMemo(() => {
    return chunkArray(products, 3);
  }, [products]);

  const _animationPlay = useMemo(() => {
    return animationPlay && productArray.length > 1;
  }, [animationPlay, productArray.length]);

  useEffect(() => {
    setIsClaimed(!!id ? true : false);
  }, [id]);

  useEffect(() => {
    setEndTime(end_time);
  }, [end_time]);

  useEffect(() => {
    if (currSwiperRef.current) {
      if (_animationPlay) {
        currSwiperRef.current.params.autoplay.delay = ANIMATION_DELAY;

        currSwiperRef.current.autoplay.start();
      } else {
        currSwiperRef.current.autoplay.stop();
      }
    }
  }, [_animationPlay]);

  useEffect(() => {
    if (productWrapperRef.current) {
      const elementWidth = productWrapperRef.current.offsetWidth; // 获取元素宽度
      setProductWidth(elementWidth / 3); // 存储宽度到状态
    }
  }, []);

  if (!info) return null;

  return (
    <ImpTracker
      dataTestId={`wid-coupon-list-item-${index}`}
      impTrackingT2={() =>
        bannerImpression({
          mod_nm: modNm || 'coupon_list',
          mod_pos: modPos || 2,
          co: {
            banner_id: id || plan_id,
            banner_type: 'coupon',
            banner_pos: index,
            url: info?.use_url
          },
          ctx: {
            global_vendor: seller_id,
            status: !!id ? 'coupon' : 'coupon_plan'
          }
        })
      }>
      <div
        ref={ref}
        className="border border-solid bg-surface-100-bg box-border border-[rgba(0,0,0,0.05)] rounded-500 px-3 pt-3 mb-5 overflow-hidden"
        style={{ minHeight: '270px' }}
        onClick={handleCouponSection}>
        {/* vendor info */}
        <div data-testid="wid-coupon-list-item-vendor-info" className="flex items-center">
          {/* logo */}
          {!!seller_logo_url && (
            <div
              data-testid="wid-coupon-list-item-vendor-logo"
              className={twMerge(
                'flex-none flex items-center justify-center flex-shrink-0',
                'rounded-full mr-2 overflow-hidden',
                'relative'
              )}>
              <CroppedImage
                width={40}
                height={40}
                className="object-cover w-10 h-10 rounded-full overflow-hidden"
                wrapperClassName="w-10 h-10 box-border rounded-full overflow-hidden"
                url={seller_logo_url}
                configs={{ renderWidth: 40, renderHeight: 40 }}
                alt={`weee seller ${seller_id} logo`}
              />
              <div
                className={twMerge(
                  'w-10 h-10 rounded-full overflow-hidden',
                  'border border-solid box-border border-surface-100-hairline',
                  'absolute top-0 bottom-0 right-0 left-0'
                )}></div>
            </div>
          )}
          {/* title & promotion info */}
          <div className="flex-1 flex flex-col">
            <div
              data-testid="wid-coupon-list-item-vendor-title"
              className="enki-body-base-medium text-surface-100-fg-default line-clamp-1">
              {seller_title}
            </div>
            {isValidArray(promotions_info) && (
              <div
                data-testid="wid-coupon-list-item-vendor-promotion-info"
                className={twMerge('flex items-start text-pricing-bg mt-1')}>
                <TagFilled className="text-xs mr-1 flex-none mt-1" />
                <div className={twMerge('enki-body-3xs-strong flex-1', isUseInCms ? 'line-clamp-1' : 'line-clamp-2')}>
                  <div className="leading-5">{promotions_info?.[0].title}</div>
                </div>
              </div>
            )}
          </div>
        </div>
        {/* 商品卡片 */}
        <div
          data-testid="wid-coupon-list-item-products-container"
          className="-mx-3 my-3"
          ref={productWrapperRef}
          style={{ height: `${productWidth}px`, minHeight: '100px' }}>
          {isValidArray(productArray) && (
            <Swiper
              onInit={onSlideChange}
              slidesPerView="auto"
              speed={ANIMATION_TRANSITION}
              loop={true}
              autoplay={_animationPlay ? { delay: ANIMATION_DELAY } : false}
              effect="fade"
              fadeEffect={{
                crossFade: true
              }}
              allowTouchMove={false}
              className={twMerge('flex justify-between')}>
              {productArray?.map((item, index) => {
                return (
                  <SwiperSlide key={`${index}`} className="flex flex-nowrap">
                    {item?.map((item, index) => {
                      return (
                        <div
                          data-testid={`wid-coupon-list-item-product-${item.id}`}
                          key={`${item?.id}-${index}`}
                          className="w-1/3 flex-shrink-0 relative"
                          style={{
                            height: `${productWidth}px`
                          }}>
                          {/* 商品title文字 */}
                          {!!productWidth && (
                            <div
                              className={twMerge('absolute bottom-200 left-0 right-0 line-clamp-2')}
                              style={{ zIndex: 2, height: '34px' }}>
                              <div
                                className={twMerge(
                                  'enki-body-2xs-medium text-reserved-true-white !leading-125 p-1 pb-200'
                                )}
                                style={{
                                  textShadow: '0px 0px 4px rgba(0, 0, 0, 0.50)'
                                }}>
                                {item.title}
                              </div>
                            </div>
                          )}
                          {/* 文字蒙层 */}
                          <div
                            className={'absolute top-0 left-0 bottom-0 right-0 z-[1]'}
                            style={{
                              background:
                                'linear-gradient(180deg, rgba(0, 0, 0, 0.01) 52.65%, rgba(0, 0, 0, 0.30) 100%)'
                            }}></div>
                          {/* 图片 */}
                          <CroppedImage
                            className="w-full h-full object-cover"
                            wrapperClassName="w-full h-full absolute object-cover top-0 left-0 right-0 z-0"
                            width={'100%'}
                            height={'100%'}
                            url={item?.image_url}
                            alt={`weee product ${item?.title}`}
                            configs={{ renderHeight: productWidth, renderWidth: productWidth }}
                          />
                        </div>
                      );
                    })}
                  </SwiperSlide>
                );
              })}
            </Swiper>
          )}
        </div>
        {/* buttons -------过期*/}
        {expired && (
          <div
            data-testid="btn-coupon-expired"
            className={twMerge(
              'flex items-center justify-center',
              'h-9 rounded-full',
              'bg-shade-neutral-base-1 text-reserved-true-white enki-body-sm-medium'
            )}>
            <div className="line-clamp-1 !leading-5">
              <Trans
                i18nKey="vendor:mkpl_coupon_btn_expired"
                values={{
                  promote: info?.promote_title
                }}
              />
            </div>
          </div>
        )}
        {/* buttons -------未过期已领取 */}
        {!expired && isClaimed && (
          <div
            data-testid="btn-coupon-claimed"
            onClick={e => handleUseCoupon(e)}
            className={twMerge(
              'flex items-center justify-center',
              'h-9 rounded-full',
              'text-[#F37519] bg-primary-surface-100-fg-default enki-body-sm-medium',
              'cursor-pointer',
              'shadow-[0px_4px_12px_0px_rgba(0,0,0,0.12),0px_0px_3px_0px_rgba(0,0,0,0.05)]'
            )}>
            <WavyCheckFilled className="mr-1 flex-none" />
            <div className={twMerge('line-clamp-1 leading-5')}>
              <Trans
                i18nKey="vendor:mkpl_coupon_btn_use"
                values={{
                  promote: info?.promote_title
                }}
              />
            </div>
            <ArrowRight className="ml-2 flex-none" />
          </div>
        )}
        {/* buttons -------未过期，未领取 */}
        {!expired && !isClaimed && (
          <div
            data-testid="btn-coupon-claim"
            onClick={handleClaimCoupon}
            className={twMerge(
              'flex items-center justify-center',
              'h-9 rounded-full px-2',
              'bg-[#F37519] text-btn-primary-fg-default enki-body-sm-medium',
              'cursor-pointer',
              'shadow-[0px_4px_12px_0px_rgba(0,0,0,0.12),0px_0px_3px_0px_rgba(0,0,0,0.05)]'
            )}>
            <PlusFilled className="w-4 h-4 mr-1 flex-none" />
            <div className="line-clamp-1 leading-5">
              <Trans
                i18nKey="vendor:mkpl_coupon_btn_claim_promote"
                values={{
                  promote: info?.promote_title
                }}
              />
            </div>
          </div>
        )}
        {/* 有限期 && subtitle */}
        <div data-testid="wid-coupon-subinfo" className=" pt-3 pb-4">
          <div className={twMerge('text-center', isUseInCms ? 'line-clamp-1' : '')}>
            {claiming ? (
              '--:--:--'
            ) : (
              <div className="-mt-3 inline-block">
                <CouponLeftTime onChangeStatus={handleSetExpired} expired={expired} endTime={endTime} />
              </div>
            )}
            {!expired && !!info?.subtitle && (
              <div className="inline enki-body-2xs text-surface-100-fg-minor">{` • `}</div>
            )}
            {!!info?.subtitle && (
              <div
                className={twMerge(
                  'inline',
                  'enki-body-2xs text-surface-100-fg-minor whitespace-pre-wrap',
                  'text-center'
                )}>
                {info?.subtitle}
              </div>
            )}
          </div>
        </div>

        <CouponDetailsModal
          index={index}
          modNm={modNm}
          showSuccessModal={showModal}
          onClose={handleClose}
          info={info}
          couponId={couponId}
          endTime={endTime}
        />
      </div>
    </ImpTracker>
  );
};

export default CouponCard;
